import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { message } from "antd";
import ApiService from "../service/api-service";
import type { EchoSessionResponse } from "../types/echo-session";

const FeedbackLoader: React.FC = () => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const loadingMessages = [
    "Setting up your feedback session...",
    "Getting everything ready...",
    "Preparing the environment...",
    "Almost there...",
    "Initializing audio and video...",
    "Setting up secure connection...",
    "Loading your personalized session...",
    "Just a few more moments...",
    "Making final preparations...",
  ];

  // Function to get a random message (not the current one)
  const getRandomMessage = () => {
    if (loadingMessages.length <= 1) return 0;
    
    let newIndex;
    do {
      newIndex = Math.floor(Math.random() * loadingMessages.length);
    } while (newIndex === currentMessageIndex);
    
    return newIndex;
  };

  useEffect(() => {
    const startSession = async () => {
        console.log("Starting session", searchParams);
      const echoId = searchParams.get("echoId");
      const audienceId = searchParams.get("audienceId");

      if (!echoId || !audienceId) {
        message.error("Invalid session parameters");
        navigate("/");
        return;
      }

      try {
        const response = await ApiService.startEchoSession(echoId, audienceId);
        
        if (response.success) {
          navigate("/feedback", {
            state: {
              response: response.data
            }
          });
        } else {
          message.error(response.message);
          navigate("/");
        }
      } catch (error) {
        message.error(error instanceof Error ? error.message : "Failed to start session");
        navigate("/");
      }
    };

    startSession();
  }, [searchParams, navigate]);

  // Rotate messages every 4 seconds with animation
  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      
      setTimeout(() => {
        setCurrentMessageIndex(getRandomMessage);
        
        setTimeout(() => {
          setIsAnimating(false);
        }, 50);
      }, 450);
      
    }, 4000);

    return () => {
      clearInterval(interval);
    };
  }, [currentMessageIndex]);

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-cyan-50 via-white to-cyan-50 flex flex-col items-center justify-center text-gray-800 z-[9999] overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-20 -left-20 w-96 h-96 bg-gradient-to-r from-cyan-200/40 to-cyan-100/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-1/3 -right-32 w-80 h-80 bg-gradient-to-r from-cyan-100/35 to-cyan-200/25 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute -bottom-24 left-1/3 w-72 h-72 bg-gradient-to-r from-cyan-200/30 to-cyan-100/40 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center max-w-lg px-6">
        {/* Enhanced loading spinner */}
        <div className="mb-10 relative">
          <div className="animate-spin rounded-full h-20 w-20 border-4 border-cyan-200 border-t-cyan-600"></div>
          <div className="absolute inset-2 animate-spin rounded-full h-16 w-16 border-2 border-cyan-100 border-t-cyan-400" style={{ animationDirection: 'reverse', animationDuration: '3s' }}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-cyan-500 rounded-full animate-pulse"></div>
        </div>
        
        {/* Message container */}
        <div className="h-24 flex items-center justify-center overflow-hidden">
          <div 
            className={`text-center max-w-lg px-6 transition-all duration-500 ease-in-out ${
              isAnimating 
                ? 'opacity-0 transform translate-y-4' 
                : 'opacity-100 transform translate-y-0'
            }`}
          >
            <h2 className="text-xl sm:text-2xl font-medium text-gray-700 leading-relaxed">
              {loadingMessages[currentMessageIndex]}
            </h2>
          </div>
        </div>
        
        {/* Gradient progress bar */}
        <div className="mt-8 w-56 h-2 bg-white/60 rounded-full overflow-hidden shadow-inner">
          <div className="h-full bg-gradient-to-r from-cyan-400 to-cyan-600 rounded-full animate-pulse"></div>
        </div>
        
        {/* Subtext */}
        <p className="mt-6 text-gray-600 text-sm font-medium">
          This usually takes less than a minute
        </p>
        
        {/* Status indicator */}
        <div className="mt-4 flex items-center space-x-2 text-gray-500 text-xs">
          <div className="w-2 h-2 bg-gradient-to-r from-cyan-500 to-cyan-600 rounded-full animate-pulse"></div>
          <span>Initializing Feedback Session</span>
        </div>
      </div>
    </div>
  );
};

export default FeedbackLoader;